import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { FORTA_BLACK, FORTA_BLUE } from '../../../../../../utils/colors';
import FHDurationInput from '../../../../../../components/base/FHDurationInput';
import FHCounter from '../../../../../../components/base/FHCounter';
import { TargetType } from '../../../../../../constants';
import { FHTooltip } from '../../../../../../components/base';
import { capitalizeFirstLetter } from '../../../../../../helpers';

import type {
  ISessionPracticedTargetTrial,
  ITreatmentPlanGoalTarget,
} from '../../../../../../utils/types';

interface TargetPracticeInputProps {
  variant: TargetType;
  isEditable: boolean;
  savingFailed: boolean;
  savingInProgress: boolean;
  trial: ISessionPracticedTargetTrial;
  target?: ITreatmentPlanGoalTarget;
  onTrialDataChange: (trialDataDiff: any) => void;
  onCounterFocus: (isFocused: boolean) => void;
}

const TargetPracticeInput: React.FC<TargetPracticeInputProps> = (props) => {
  const {
    variant,
    trial,
    target,
    isEditable = true,
    savingFailed = false,
    savingInProgress = false,
    onTrialDataChange,
    onCounterFocus,
  } = props;
  const [numSuccesses, setNumSuccesses] = useState<number>(0);
  const [numFailures, setNumFailures] = useState<number>(0);
  const [numInstances, setNumInstances] = useState<number>(0);

  useEffect(() => {
    if (!trial) return;
    if (variant === TargetType.SuccessFailure) {
      if (Number.isInteger(trial?.attempts)) {
        setNumFailures(trial.attempts - (trial?.expected_response ?? 0));
      }
      if (Number.isInteger(trial?.expected_response)) {
        setNumSuccesses(trial.expected_response);
      }
    }
    if (variant === TargetType.Instances) {
      if (Number.isInteger(trial?.occurrence)) {
        setNumInstances(trial.occurrence);
      }
    }
    if (variant === TargetType.InstancesWithDuration) {
      //TBD update this after api endpoint is ready
    }
  }, [trial, variant]);

  useEffect(() => {
    if (savingFailed) {
      if (variant === TargetType.SuccessFailure) {
        if (Number.isInteger(trial?.attempts)) {
          setNumFailures(trial.attempts - (trial?.expected_response ?? 0));
        }
        if (Number.isInteger(trial?.expected_response)) {
          setNumSuccesses(trial.expected_response);
        }
      }
      if (variant === TargetType.Instances) {
        if (Number.isInteger(trial?.occurrence)) {
          setNumInstances(trial.occurrence);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingFailed]);

  const renderInstanceWithDuration = () => {
    return (
      <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
          <Typography
            sx={{
              fontSize: '16px',
              textAlign: 'left',
              color: FORTA_BLACK,
              lineHeight: '24px',
            }}
          >
            <strong>Duration</strong> (of instance)
          </Typography>
          <FHTooltip
            title="Your BCBA is requiring you to record the instance’s duration. Each recorded duration will count as an instance."
            arrow
            placement="top"
            maxwidth={310}
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ ml: 1, mb: '2px', color: FORTA_BLUE.primary }}
            />
          </FHTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            gap: '8px',
          }}
        >
          <FHDurationInput
            dataTestId={`InputDurationTarget${capitalizeFirstLetter(
              trial?.target?.label?.split(' ')?.join(''),
            )}`}
            isDisabled={!isEditable || savingInProgress}
            reset={savingInProgress}
            durationType={
              target?.event_duration_measurement_unit
                ? `${target.event_duration_measurement_unit.toLowerCase()}s`
                : ''
            }
            onAdd={(durationToAdd) => {
              onTrialDataChange({
                instances: [...(trial?.instances ?? []), { duration: durationToAdd }],
                occurrence: (trial?.instances?.length ?? 0) + 1,
              });
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderSuccessFailure = () => {
    return (
      <>
        <Box
          sx={{
            flex: '1 0 auto',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '16px',
          }}
        >
          <Box
            sx={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                textAlign: 'center',
                color: FORTA_BLACK,
              }}
            >
              Unsuccessful
            </Typography>
            <FHCounter
              dataTestId={`UnsuccessfulTarget${capitalizeFirstLetter(
                trial?.target?.label?.split(' ')?.join(''),
              )}`}
              disabled={!isEditable || savingInProgress}
              count={numFailures}
              onFocus={onCounterFocus}
              onCountChange={(newCount) => {
                onTrialDataChange({ attempts: numSuccesses + newCount });
                setNumFailures(newCount);
              }}
            />
          </Box>
          <Box
            sx={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                textAlign: 'center',
                color: FORTA_BLACK,
              }}
            >
              Successful
            </Typography>
            <FHCounter
              dataTestId={`SuccessfulTarget${capitalizeFirstLetter(
                trial?.target?.label?.split(' ')?.join(''),
              )}`}
              disabled={!isEditable || savingInProgress}
              count={numSuccesses}
              onFocus={onCounterFocus}
              onCountChange={(newCount) => {
                onTrialDataChange({
                  expected_response: newCount,
                  attempts: newCount + numFailures,
                });
                setNumSuccesses(newCount);
              }}
            />
          </Box>
        </Box>
        <Typography
          fontSize={16}
          sx={{
            mt: 2,
            textAlign: 'center',
            color: FORTA_BLACK,
          }}
        >
          Total attempts: {numSuccesses + numFailures}
        </Typography>
      </>
    );
  };

  const renderInstances = () => {
    return (
      <Box
        sx={{
          flex: '1 0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            textAlign: 'center',
            color: FORTA_BLACK,
          }}
        >
          Instances
        </Typography>
        <FHCounter
          dataTestId={`InstancesTarget${capitalizeFirstLetter(
            trial?.target?.label?.split(' ')?.join(''),
          )}`}
          disabled={!isEditable || savingInProgress}
          onFocus={onCounterFocus}
          count={numInstances}
          onCountChange={(newCount) => {
            onTrialDataChange({ occurrence: newCount });
            setNumInstances(newCount);
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      {variant === TargetType.Instances
        ? renderInstances()
        : variant === TargetType.InstancesWithDuration
          ? renderInstanceWithDuration()
          : variant === TargetType.SuccessFailure && renderSuccessFailure()}
    </Box>
  );
};

export default TargetPracticeInput;
